import {
  Component,
  JSX,
  Show,
  createSignal,
  createEffect,
  onCleanup,
  children as resolveChildren,
} from "solid-js";

export interface DropdownProps {
  /**
   * The element that opens the menu. Given a function instead, it is called
   * with the open state and owns `aria-haspopup`/`aria-expanded` itself —
   * the only way to put them on the focusable element rather than on the
   * non-focusable wrapper below, which is what a screen reader reads.
   */
  trigger: JSX.Element | ((isOpen: () => boolean) => JSX.Element);
  class?: string;
  /** Which edge of the trigger the menu aligns to. Defaults to 'left'. */
  align?: "left" | "right";
  /** Which side of the trigger the menu opens on. Defaults to 'bottom'. */
  placement?: "bottom" | "top";
  /** Extra classes for the menu panel itself (e.g. to widen it). */
  menuClass?: string;
  /**
   * CSS selector used to focus an element inside the menu when it opens.
   * Defaults to the first non-disabled menuitem.
   */
  initialFocusSelector?: string;
  children: JSX.Element;
}

export interface DropdownItemProps {
  onClick?: () => void;
  disabled?: boolean;
  class?: string;
  title?: string;
  "aria-current"?: "true";
  /**
   * Overrides the accessible name for an item whose content is an icon
   * alone. Preferred over sr-only text here: text inside a menuitem is what
   * the menu is filtered and matched by, and a hidden copy of the label
   * would make a row match twice.
   */
  "aria-label"?: string;
  children: JSX.Element;
}

const MENU_ITEM_SELECTOR = '[role="menuitem"]:not([aria-disabled="true"])';

/**
 * Roving focus for ArrowDown/ArrowUp inside a menu, wrapping at both ends.
 * Shared by Dropdown and CardContextMenu.
 */
export const moveMenuFocus = (
  menu: HTMLElement | undefined,
  direction: 1 | -1,
): void => {
  const items = menu?.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR);
  if (!items?.length) return;

  const currentIndex = Array.from(items).findIndex(
    (item) => item === document.activeElement,
  );
  let nextIndex: number;

  if (direction === 1) {
    nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
  } else {
    nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
  }

  items[nextIndex]?.focus();
};

/**
 * Focuses the first matching menu item on the next animation frame. Falls
 * back to focusing the menu root itself (if focusable) when no item
 * matches, so a fully-disabled menu is still keyboard-dismissable.
 */
export const focusFirstMenuItem = (
  menu: HTMLElement | undefined,
  selector: string = MENU_ITEM_SELECTOR,
): void => {
  requestAnimationFrame(() => {
    const target = menu?.querySelector<HTMLElement>(selector);
    if (target) {
      target.focus();
    } else {
      menu?.focus();
    }
  });
};

export const DropdownItem: Component<DropdownItemProps> = (props) => {
  return (
    <div
      role="menuitem"
      tabIndex={props.disabled ? -1 : 0}
      title={props.title}
      class={
        "px-3 py-2 text-sm cursor-pointer " +
        (props.disabled
          ? "text-ink-3 cursor-not-allowed"
          : "text-ink hover:bg-sunken focus:bg-sunken focus-ring") +
        " " +
        (props.class ?? "")
      }
      onClick={() => !props.disabled && props.onClick?.()}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !props.disabled) {
          e.preventDefault();
          // Dispatch a real click rather than calling the handler straight:
          // the menu closes on a click landing inside a menuitem, so a direct
          // call activates the item and leaves the menu standing open.
          e.currentTarget.click();
        }
      }}
      aria-disabled={props.disabled}
      aria-current={props["aria-current"]}
      aria-label={props["aria-label"]}
    >
      {props.children}
    </div>
  );
};

export const Dropdown: Component<DropdownProps> = (props) => {
  const [isOpen, setIsOpen] = createSignal(false);
  let containerRef: HTMLDivElement | undefined;
  let menuRef: HTMLDivElement | undefined;
  let triggerWrapRef: HTMLDivElement | undefined;

  const closeAndReturnFocus = () => {
    setIsOpen(false);
    requestAnimationFrame(() => {
      const btn =
        triggerWrapRef?.querySelector<HTMLElement>("button, [tabindex]");
      btn?.focus();
    });
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (containerRef && !containerRef.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isOpen()) return;

    if (e.key === "Escape") {
      e.preventDefault();
      closeAndReturnFocus();
      return;
    }

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      moveMenuFocus(menuRef, e.key === "ArrowDown" ? 1 : -1);
    }
  };

  createEffect(() => {
    if (isOpen()) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);

      const selector = props.initialFocusSelector ?? MENU_ITEM_SELECTOR;
      focusFirstMenuItem(menuRef, selector);

      onCleanup(() => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleKeyDown);
      });
    }
  });

  // A function trigger is invoked here rather than handed to `children()` as
  // an accessor: that would call it with no argument and the trigger would
  // read `isOpen` off `undefined`.
  const ownsTriggerAria = () => typeof props.trigger === "function";
  const resolved = resolveChildren(() => {
    const t = props.trigger;
    return typeof t === "function" ? t(isOpen) : t;
  });

  return (
    <div
      ref={containerRef}
      class={"relative inline-block " + (props.class ?? "")}
    >
      <div
        ref={triggerWrapRef}
        onClick={() => setIsOpen(!isOpen())}
        aria-haspopup={ownsTriggerAria() ? undefined : "menu"}
        aria-expanded={ownsTriggerAria() ? undefined : isOpen()}
      >
        {resolved()}
      </div>

      <Show when={isOpen()}>
        <div
          ref={menuRef}
          role="menu"
          class={
            "absolute z-10 min-w-[160px] bg-surface border border-line rounded shadow-overlay py-1 animate-fade-in " +
            (props.placement === "top" ? "bottom-full mb-1 " : "mt-1 ") +
            (props.align === "right" ? "right-0" : "left-0") +
            " " +
            (props.menuClass ?? "")
          }
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('[role="menuitem"]')) {
              setIsOpen(false);
            }
          }}
        >
          {props.children}
        </div>
      </Show>
    </div>
  );
};
